<template>
  <!-- 登录页面：全屏显示，无布局 -->
  <div v-if="isLoginPage" class="h-screen">
    <router-view />
  </div>

  <!-- 主应用：带布局 -->
  <div v-else class="h-screen flex flex-col bg-gray-50">
    <!-- 顶部导航 -->
    <Header 
      :show-menu-button="true"
      @menu-click="handleMenuClick"
    />
    
    <div class="flex flex-1 overflow-hidden relative">
      <!-- 移动端遮罩层 -->
      <div 
        v-if="sidebarOpen"
        class="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
        @click="setSidebarOpen(false)"
      />
      
      <!-- 左侧导航 -->
      <div :class="[
        sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        'md:translate-x-0 fixed md:relative z-50 md:z-auto transition-transform duration-300 ease-in-out'
      ]">
        <Sidebar 
          :active-section="activeSection" 
          @section-change="handleSectionChange" 
        />
      </div>
      
      <!-- 主内容区域 -->
      <div class="flex-1 flex flex-col overflow-hidden">
        <!-- 填报信息模块的子导航 -->
        <SubNavigation 
          v-if="activeSection === 'report'"
          :active-tab="activeTab" 
          @tab-change="setActiveTab" 
        />
        
        <!-- 内容区域 -->
        <div class="flex-1 overflow-auto bg-white">
          <div :class="[
            activeSection === 'todos' || activeSection === 'achievements' 
              ? 'p-3 md:px-4 md:py-6' 
              : 'p-3 md:p-6'
          ]">
            <router-view />
          </div>
          <!-- 页脚 -->
          <Footer />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, watch, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAppStore } from './stores'
import { storeToRefs } from 'pinia'
import { getFeedbackList } from '@/api/teacherStatus'
import { getAuthToken } from '@/api/auth'

// 导入组件
import Header from './components/layout/Header.vue'
import Sidebar from './components/layout/Sidebar.vue'
import Footer from './components/layout/Footer.vue'
import SubNavigation from './components/layout/SubNavigation.vue'

// 使用store
const store = useAppStore()
const { activeSection, activeTab, sidebarOpen } = storeToRefs(store)
const { setActiveSection, setActiveTab, setSidebarOpen } = store

// 路由
const route = useRoute()
const router = useRouter()

// 判断是否是登录页面
const isLoginPage = computed(() => route.path === '/login')

// 方法
const handleMenuClick = () => {
  store.toggleSidebar()
}

const handleSectionChange = (section) => {
  setActiveSection(section)
  setSidebarOpen(false) // 移动端选择后关闭侧边栏
  /* 顺序与 Sidebar 保持一致 */
  if (section === 'report') router.push('/')
  else if (section === 'achievements') router.push('/my-achievements')
  else if (section === 'profile') router.push('/personal-info')
  else if (section === 'department') router.push('/department')
  else if (section === 'teacher-status') router.push('/teacher-status')
  else if (section === 'info-overview') router.push('/info-overview')
  else if (section === 'permission-settings') router.push('/permission-settings')
}

const hasLoadedPendingFeedback = ref(false)

async function loadPendingFeedbackCount() {
  if (hasLoadedPendingFeedback.value) return
  const token = getAuthToken()
  if (!token) return
  try {
    const res = await getFeedbackList({ page: 1, pageSize: 100 })
    const data = res?.data ?? res
    const records = data?.records || []
    const pending = records.filter(r => r.processStatus === 1 || r.processStatus === '1').length
    store.setPendingFeedbackCount(pending)
    hasLoadedPendingFeedback.value = true
  } catch (error) {
    console.warn('加载待处理反馈数量失败:', error)
  }
}

onMounted(async () => {
  if (route.path === '/login') return
  await loadPendingFeedbackCount()
})

// 路由与 store 同步：当访问子路由时，同步激活侧边栏高亮
watch(
  () => route.path,
  (p) => {
    if (p === '/') setActiveSection('report')
    else if (p === '/my-achievements') setActiveSection('achievements')
    else if (p === '/personal-info') setActiveSection('profile')
    else if (p === '/department') setActiveSection('department')
    else if (p === '/teacher-status') setActiveSection('teacher-status')
    else if (p === '/info-overview') setActiveSection('info-overview')
    else if (p === '/permission-settings') setActiveSection('permission-settings')
    else setActiveSection('report')
  },
  { immediate: true }
)

watch(
  () => route.path,
  async (p) => {
    if (p !== '/login') await loadPendingFeedbackCount()
  }
)
</script>
